import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { DueInput } from "../components/DueInput";
import { ArrowLeft, Plus, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { post } from "../lib/api";
import { useSpaces, useRequestTypes, type FieldDef, type Person, type RequestType } from "../lib/work";
import { useOrg } from "./admin/organisation";
import { ErrorNote, Field, Modal } from "../components/ui";
import { PersonPicker } from "../components/work";
import { AiCreate, type AiDraft } from "./insights";

function FormField({ f, value, onChange }: { f: FieldDef; value: unknown; onChange: (v: unknown) => void }) {
  const org = useOrg();
  const [people, setPeople] = useState<Person[]>([]);
  const common = { className: "input", required: f.required, "aria-label": f.label };
  let input: React.ReactNode;
  switch (f.type) {
    case "textarea": input = <textarea {...common} className="input min-h-[5rem]" value={String(value ?? "")} onChange={(e) => onChange(e.target.value)} />; break;
    case "number": input = <input {...common} type="number" inputMode="decimal" value={String(value ?? "")} onChange={(e) => onChange(e.target.value)} />; break;
    case "date": input = <input {...common} type="date" value={String(value ?? "")} onChange={(e) => onChange(e.target.value)} />; break;
    case "datetime": input = <input {...common} type="datetime-local" value={String(value ?? "")} onChange={(e) => onChange(e.target.value)} />; break;
    case "email": input = <input {...common} type="email" value={String(value ?? "")} onChange={(e) => onChange(e.target.value)} />; break;
    case "phone": input = <input {...common} type="tel" value={String(value ?? "")} onChange={(e) => onChange(e.target.value)} />; break;
    case "checkbox": input = <input type="checkbox" className="h-5 w-5" checked={!!value} onChange={(e) => onChange(e.target.checked)} />; break;
    case "select": input = <select {...common} value={String(value ?? "")} onChange={(e) => onChange(e.target.value)}><option value="">Choose…</option>{f.options?.map((o) => <option key={o}>{o}</option>)}</select>; break;
    case "site": input = <select {...common} value={String(value ?? "")} onChange={(e) => onChange(e.target.value)}><option value="">Choose…</option>{org.data?.sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>; break;
    case "company": input = <select {...common} value={String(value ?? "")} onChange={(e) => onChange(e.target.value)}><option value="">Choose…</option>{org.data?.companies.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>; break;
    case "department": input = <select {...common} value={String(value ?? "")} onChange={(e) => onChange(e.target.value)}><option value="">Choose…</option>{org.data?.departments.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>; break;
    case "team": input = <select {...common} value={String(value ?? "")} onChange={(e) => onChange(e.target.value)}><option value="">Choose…</option>{org.data?.teams.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>; break;
    case "person": input = <PersonPicker multiple={false} value={people} onChange={(p) => { setPeople(p); onChange(p[0]?.id); }} />; break;
    default: input = <input {...common} value={String(value ?? "")} onChange={(e) => onChange(e.target.value)} />;
  }
  return <Field label={`${f.label}${f.required ? " *" : ""}`} hint={f.help}>{input}</Field>;
}

function RequestForm({ t, onBack, onDone, initial }: { t: RequestType; onBack: () => void; onDone: (workItemId: string) => void; initial?: Record<string, unknown> }) {
  const [data, setData] = useState<Record<string, unknown>>(initial ?? {});
  const m = useMutation({
    mutationFn: () => {
      const clean = Object.fromEntries(Object.entries(data).map(([k, v]) => [k, t.form.find((f) => f.key === k)?.type === "datetime" && v ? new Date(String(v)).toISOString() : v]));
      return post<{ workItemId: string; ref: string }>("/api/requests", { typeKey: t.key, data: clean });
    },
    onSuccess: (r) => onDone(r.workItemId),
  });
  return (
    <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); m.mutate(); }}>
      <button type="button" className="btn !min-h-0 !py-1" onClick={onBack}><ArrowLeft size={14} /> All request types</button>
      <div>
        <h3 className="font-semibold">{t.name}</h3>
        {t.description && <p className="muted text-sm">{t.description}</p>}
        <p className="muted text-xs">Goes to {t.spaceName}{t.slaHours ? ` · answered within ${t.slaHours < 24 ? `${t.slaHours}h` : `${Math.round(t.slaHours / 24)} day(s)`}` : ""}</p>
      </div>
      <ErrorNote error={m.error} />
      {t.form.map((f) => <FormField key={f.key} f={f} value={data[f.key]} onChange={(v) => setData((d) => ({ ...d, [f.key]: v }))} />)}
      <button className="btn btn-primary w-full justify-center" disabled={m.isPending}>{m.isPending ? "Sending…" : "Submit request"}</button>
    </form>
  );
}

function QuickTask({ onDone, initialTitle }: { onDone: (id: string) => void; initialTitle?: string }) {
  const spaces = useSpaces();
  const [title, setTitle] = useState(initialTitle ?? "");
  const [spaceId, setSpaceId] = useState("");
  const [assignees, setAssignees] = useState<Person[]>([]);
  const [due, setDue] = useState("");
  const [priority, setPriority] = useState("P3");
  const space = spaceId || spaces.data?.items.find((s) => s.key === "general")?.id || "";
  const m = useMutation({
    mutationFn: () => post<{ id: string }>("/api/work/items", { spaceId: space, title, assigneeIds: assignees.map((a) => a.id), dueAt: due || undefined, priority }),
    onSuccess: (r) => onDone(r.id),
  });
  return (
    <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); m.mutate(); }}>
      <ErrorNote error={m.error} />
      <Field label="What needs doing?"><input className="input" autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Call the supplier about the pool pump" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Space">
          <select className="input" value={space} onChange={(e) => setSpaceId(e.target.value)}>{spaces.data?.items.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
        </Field>
        <Field label="Priority">
          <select className="input" value={priority} onChange={(e) => setPriority(e.target.value)}>{[["P1", "Urgent"], ["P2", "High"], ["P3", "Normal"], ["P4", "Low"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        </Field>
      </div>
      <Field label="Assign to (optional — leave empty for yourself)"><PersonPicker value={assignees} onChange={setAssignees} /></Field>
      <Field label="Due (optional)"><DueInput value={due || null} onChange={(iso) => setDue(iso ?? "")} /></Field>
      <button className="btn btn-primary w-full justify-center" disabled={!title.trim() || !space || m.isPending}>Create task</button>
    </form>
  );
}

/** Universal CREATE: a task, or any configured request type. */
export function CreateSheet({ onClose }: { onClose: () => void }) {
  const types = useRequestTypes();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [chosen, setChosen] = useState<RequestType | "task" | null>(null);
  const [draft, setDraft] = useState<AiDraft | null>(null);
  const useDraft = (d: AiDraft) => {
    setDraft(d);
    if (d.typeKey === "task") setChosen("task");
    else setChosen(types.data?.items.find((t) => t.key === d.typeKey) ?? null);
  };
  const grouped = useMemo(() => {
    const list = (types.data?.items ?? []).filter((t) => !q || `${t.name} ${t.category} ${t.description}`.toLowerCase().includes(q.toLowerCase()));
    const g = new Map<string, RequestType[]>();
    list.forEach((t) => g.set(t.category, [...(g.get(t.category) ?? []), t]));
    return [...g.entries()];
  }, [types.data, q]);
  const done = (id: string) => {
    void qc.invalidateQueries();
    onClose();
    void navigate({ to: "/work/$id", params: { id } });
  };
  return (
    <Modal title="Create" onClose={onClose}>
      {chosen === null && (
        <div className="space-y-3">
          <AiCreate onDraft={useDraft} />
          <button className="panel flex w-full items-center gap-3 p-3 text-left hover:border-brand-400" onClick={() => setChosen("task")}>
            <Plus size={18} /> <span><span className="block font-medium">Task</span><span className="muted text-xs">Something you or a colleague needs to do</span></span>
          </button>
          <div className="relative">
            <Search size={16} className="muted absolute left-3 top-2.5" />
            <input className="input !pl-9" placeholder="What do you need? e.g. laptop, freeze membership, purchase" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          {grouped.map(([cat, list]) => (
            <div key={cat}>
              <div className="muted mb-1 text-xs font-semibold uppercase tracking-wide">{cat}</div>
              <ul className="grid gap-1.5 sm:grid-cols-2">
                {list.map((t) => (
                  <li key={t.key}>
                    <button className="panel w-full p-2.5 text-left text-sm hover:border-brand-400" onClick={() => setChosen(t)}>
                      <span className="block font-medium">{t.name}</span>
                      {t.slaHours && <span className="muted text-xs">within {t.slaHours < 24 ? `${t.slaHours}h` : `${Math.round(t.slaHours / 24)}d`}</span>}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
      {chosen === "task" && <><button className="btn !min-h-0 !py-1" onClick={() => setChosen(null)}><ArrowLeft size={14} /> Back</button><QuickTask onDone={done} initialTitle={draft?.title ?? undefined} /></>}
      {chosen && chosen !== "task" && (
        <>
          {draft && draft.typeKey === chosen.key && <p className="rounded bg-violet-500/10 p-2 text-xs">Fusion AI filled this in{draft.why ? ` — ${draft.why}` : ""}. Check every field before submitting.</p>}
          <RequestForm key={chosen.key + (draft?.typeKey === chosen.key ? "-ai" : "")} t={chosen} initial={draft?.typeKey === chosen.key ? draft.data : undefined} onBack={() => { setChosen(null); setDraft(null); }} onDone={done} />
        </>
      )}
    </Modal>
  );
}
